import React, { useEffect, useMemo, useState } from 'react';
import { error as logError } from "../../../../../utils/logger";
import { createPortal } from 'react-dom';
import styles from "./Step1.module.css"
import { useProducts } from "../../../../../context/ProductContext";
import { useCartContext } from "../../../../../context/CartContext";
import ProductSmall from "../../../../../components/ProductCards/ProductSmall/ProductSmall";
import CartProductCard from "../../../../../components/ProductCards/Cart/CartProductCard";
import CartExtraCard from '../../../../../components/ProductCards/Cart/CartExtraCart';
import CartVenueCard from '../../../../../components/ProductCards/Cart/CartVenueCard';
import SecondaryButton from "../../../../../components/Button/SecondaryButton/SecondaryButton";
import SubmitButton from "../../../../../components/Button/SubmitButton/SubmitButton";
import { useTimeModal } from '../../../../../context/TimeModalContext';
import { timeToMinutes } from '../../../../../utils/calendar';
import { formatCurrency } from '../../../../../utils/prices';
import { useTranslation } from 'react-i18next';
import { PencilLine, ChevronLeft, ChevronRight } from 'lucide-react';
import { useFilterContext } from '../../../../../context/FilterContext';
import { StepProps } from '../../../../../types/checkout.types';
import { useHorizontalScroll } from '../../../../../hooks/useHorizontalScroll';
import { getCartBlockPrice, getCartPriceSummary, getCartPriceSummaryCurrency, getGroupedBlockCount, getSvozBlockCount, groupCartData, hasTransportForItems } from '../../../../../utils/cart';
import { normalizeDate } from '../../../../../utils/calendar';
import { venues } from '../../../../../constants/venues';
import { useStorageItems } from '../../../../../context/StorageItemsContext';
import { exportCartPdf } from '../../../../../services/exportService';
import { czk_eur_rate } from '../../../../../constants/prices';
import { usePreferences } from '../../../../../context/PreferencesContext';
import { filterUpsell } from '../../../../../utils/filters';
import { useActivities } from '../../../../../context/ActivitiesContext';
import CartActivityCard from '../../../../../components/ProductCards/Cart/CartActivityCard';

/**
* A Checkout  step 1 component.
* Contains list of products in cart and recommended products
*
* @param props StepProps
* @param {() => void} props.onContinue - Increment step handler
* @returns TSX.Element
*/
const Step1 = ({ onContinue, actionsContainer }: StepProps) => {
    const { cartData, reloadCart } = useCartContext();
    const { i18n, t } = useTranslation(); // Translation
    const { allProducts, packages, loading, reloadProducts } = useProducts() // Products
    const { items, reloadItems, loading: loadingItems } = useStorageItems(); // Storage items
    const { activities, reloadActivities, loading: loadingActivities } = useActivities(); // Activities
    const { selectedCurrency } = usePreferences(); // Current selected currency
    const [transportEx, setTransportEx] = useState<boolean>(false);
    // Reload products if they are loaded but dont appear
    useEffect(() => {
        if (!loading && !allProducts) {
            reloadProducts();
        }
        if (!loadingItems && !items) {
            reloadItems();
        }
        if (!loadingActivities && !activities) {
            reloadActivities();
        }
    }, [loading, loadingItems, loadingActivities])

    useEffect(() => {
        if (!cartData?.items) return;
        const hasTransport = hasTransportForItems(cartData.items, {
            sales: allProducts,
            packages,
            storage: items,
        });
        setTransportEx(hasTransport);
    }, [cartData?.items, allProducts, packages, items]);
    const { recommended, setRecommended } = useFilterContext(); // Filter data
    const { scrollBlockRef, scrollLeft, maxScroll, scrollOne, startHold, stopHold }  = useHorizontalScroll(recommended?.length, allProducts.length); // Scroll hook
    const { open, setTimeTarget } = useTimeModal(); // Time modal toggle 
    const groupedCart = groupCartData(cartData ?? null) // Grouped cart data by date and time
    const groupedBlocksCount = getGroupedBlockCount(groupedCart);
    const priceSummary = useMemo(
        () => getCartPriceSummary(cartData, groupedBlocksCount, getSvozBlockCount(groupedCart, { sales: allProducts, packages, storage: items })),
        [cartData, groupedBlocksCount, groupedCart, allProducts, packages, items]
    );
    const priceSummaryCurrency = useMemo(
        () => getCartPriceSummaryCurrency(priceSummary, selectedCurrency, czk_eur_rate),
        [priceSummary, selectedCurrency]
    );

    const [filtered, setFiltered] = useState<number[]>([])
    useEffect(()=>{
        const filt = filterUpsell(allProducts);
        setFiltered(filt);
    },[allProducts])

    // Listener for global mouse/touch up to stop hold scrolling
    const startHoldGlobal = (dir: -1 | 1) => {
        startHold(dir);
        const stop = () => {
            stopHold();
            window.removeEventListener("mouseup", stop);
            window.removeEventListener("touchend", stop);
            window.removeEventListener("touchcancel", stop);
        };
        window.addEventListener("mouseup", stop, { once: true });
        window.addEventListener("touchend", stop, { once: true });
        window.addEventListener("touchcancel", stop, { once: true });
    };  
    
    const blockLookup = useMemo(
        () => ({
            sales: allProducts,
            packages,
            storage: items,
        }),
        [allProducts, packages, items]
    );


    return (
        <>
            <div className={styles.CheckoutContent}>
                <div className={styles.Recommended}>
                    <p className={styles.RecommendedTitle}>{t("checkout.steps.1.recommend_title")}</p>
                    <div className={styles.RecommendedProducts} ref={scrollBlockRef}>
                        {[...allProducts, ...packages].filter((product, i) => filtered?.includes(product.id)).map((product, i) => {
                            if (i < 10) return <ProductSmall product={product} type={product.type ?? "sales"} />
                        })}
                    </div>

                    <div className={styles.ScrolButtonsWrap}>
                        <div className={styles.ScrollButtons}>
                            <button
                            type="button"
                            onClick={() => scrollOne(-1)}
                            onMouseDown={() => startHoldGlobal(-1)}
                            onMouseUp={stopHold}
                            onTouchStart={(e) => { e.preventDefault(); startHoldGlobal(-1); }}
                            onTouchEnd={stopHold}
                            disabled={scrollLeft <= 0}
                            >
                                <ChevronLeft />
                            </button>
                            <button
                            type="button"
                            onClick={() => scrollOne(1)}
                            onMouseDown={() => startHoldGlobal(1)}
                            onMouseUp={stopHold}
                            onTouchStart={(e) => { e.preventDefault(); startHoldGlobal(1); }}
                            onTouchEnd={stopHold}
                            disabled={scrollLeft >= maxScroll}
                            >
                                <ChevronRight />
                            </button>
                        </div>
                    </div>

                </div>
                <p className={styles.CartProductsTitle}>{t("checkout.steps.1.cart_review_title")}</p>
                <div className={styles.CartProducts}>
                    {Object.entries(groupedCart)
                        .sort(([dateA], [dateB]) => new Date(dateA).getTime() - new Date(dateB).getTime()) // Sort the dates ( substracting them gives - / + which sort uses for sorting)
                        .map(([date, times]) =>  // Loop through the dates
                            Object.entries(times)
                                .sort(([timeA], [timeB]) => {
                                    // Turn the times into minutes and sort
                                    const minutesA = timeToMinutes(timeA.split("-")[0]);
                                    const minutesB = timeToMinutes(timeB.split("-")[0]);
                                    return minutesA - minutesB;
                                })
                                .map(([time, products], i) => {// Loop throught the times
                                    const blockPrice = getCartBlockPrice(products, blockLookup);
                                    
                                    return (
                                        <React.Fragment key={`${date}-${time}`}>
                                            {/* Show date only for the first time group of each date */}
                                            {i === 0 && <p className={styles.Date}>{date}</p>}

                                            <div className={styles.CartDeliveryTime}>
                                                <p className={styles.CartTime}>{time}</p>
                                                <div
                                                    className={styles.CartTimeEdit}
                                                    onClick={() => {
                                                        setTimeTarget({ type: "group", date: normalizeDate(date), time: time });
                                                        open();
                                                    }}
                                                >
                                                    <PencilLine />
                                                </div>
                                            </div>
                                            {products.map((prod, k) => {
                                                if (prod.category === "sales") {
                                                    const product = allProducts.find(p => p.id === Number(prod.id));
                                                    if (!product) return null;
                                                    return (<CartProductCard key={`${date}-${time}-${prod.id}-${k}`} product={product} date={date} time={time} popup={false} />);
                                                } else if (prod.category === "package") {
                                                    const product = packages.find(p => p.id === Number(prod.id));
                                                    if (!product) return null;
                                                    return (<CartProductCard key={`${date}-${time}-${prod.id}-${k}`} product={product} date={date} time={time} type="package" popup={false} />);
                                                } else if (prod.category === "venue") {
                                                    const product = venues.find(p => p.id === Number(prod.id));
                                                    if (!product) return null;
                                                    return (<CartVenueCard key={`${date}-${time}-${prod.id}-${k}`} venue={product} date={date} time={time} />);
                                                } else if (prod.category === "storage") {
                                                    const product = items.find(p => p.id === Number(prod.id));
                                                    if (!product) return null;
                                                    return (<CartExtraCard key={`${date}-${time}-${prod.id}-${k}`} product={product} date={date} time={time} popup={false} />);
                                                } else if (prod.category === "activities") {
                                                    const product = activities.find(p => p.id === Number(prod.id));
                                                    if (!product) return null;
                                                    return (<CartActivityCard key={`${date}-${time}-${prod.id}-${k}`} product={product} date={date} time={time} popup={false} />);
                                                }
                                            })}
                                            <div className={styles.SingleDelivery}>
                                                <p>{cartData?.delivery_item?.name?.trim() ? cartData.delivery_item.name : t("cart.summary_section.delivery")}</p>
                                                <p>{t("products.no_dph_short")} {formatCurrency(priceSummaryCurrency.deliveryNoTax, selectedCurrency)}</p>

                                            </div>
                                        </React.Fragment>
                                    );
                                })
                        )
                    }
                    {transportEx && (
                        <div className={styles.SingleDelivery}>
                            <p>{t("cart.summary_section.collection")}</p>
                            <p>{t("products.no_dph_short")} {formatCurrency(priceSummaryCurrency.collectionNoTax, selectedCurrency)}</p>
                        </div>
                    )}
                </div>
                
                {actionsContainer && createPortal(
                    <div className={styles.Actions}>
                        <SecondaryButton
                            text={t("buttons.pdf")}
                            showChevron={false}
                            colorVariant="primary"
                            onClick={async () => {
                                try {
                                    const blob = await exportCartPdf(i18n.language);
                                    const url = URL.createObjectURL(blob);
                                    window.open(url, '_blank');
                                } catch (err) {
                                    logError('Export PDF selhal:', err);
                                }
                            }}
                        />
                        <SubmitButton
                            text={t("buttons.proceed")}
                            onClick={onContinue}
                            className={styles.ContinueButton}
                        />
                    </div>,
                    actionsContainer
                )}
                <p className={styles.Hint}>{t("checkout.explain")}</p>
            </div>
        </>
    )
}

export default Step1;
